import { useEffect, useRef, useState, useCallback } from "react";
import { BACKEND_BASE } from "../lib/backendBase";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { MagnifyingGlass, X, MapPin, CircleNotch, Buildings } from "@phosphor-icons/react";
import { fmtET, etDateKey, etDateKeyFromMs } from "../lib/etTime";

/**
 * NavbarTruckSearch — global "Find your favorite truck" autocomplete.
 *
 * Fuzzy backend search (`/api/trucks/search`) returns matches across
 * the entire published roster, NOT just trucks currently posted on
 * the map. Click behavior depends on each result's status:
 *
 *   - "live"      → navigate to /map?truck=<id> (the map opens the pin)
 *   - "scheduled" → navigate to /map?truck=<id> (pin shows the next time)
 *   - "idle"      → navigate to /t/<slug> (the public profile)
 *
 * Tolerates weird spelling via Levenshtein on the server, also matches
 * on initialisms ("JOG" → "Joe On The Go") and cuisine tags.
 */
const FILE_BASE = (BACKEND_BASE || "").replace(/\/$/, "");
const fileUrl = (p) => (p ? `${FILE_BASE}/api/files/${p}` : null);

function statusLabel(r) {
  // Expansion-engine surfaces — distinct from the live-truck statuses
  // so customers can tell at a glance "this is a coming-soon profile,
  // not a live truck".
  if (r.source === "city_teaser") {
    return { text: "Explore city", cls: "bg-primary/15 text-primary" };
  }
  if (r.source === "preloaded" || r.status === "preloaded") {
    return {
      text: r.city_name ? `Coming soon · ${r.city_name}` : "Coming soon",
      cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
    };
  }
  if (r.status === "live") return { text: "Live now", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200" };
  if (r.status === "scheduled" && r.next_post_starts_at) {
    try {
      const sameDay = etDateKey(r.next_post_starts_at) === etDateKeyFromMs(Date.now());
      const opt = sameDay
        ? { hour: "numeric", minute: "2-digit" }
        : { weekday: "short", hour: "numeric", minute: "2-digit" };
      const label = fmtET(r.next_post_starts_at, opt);
      if (!label) return { text: "Scheduled", cls: "bg-amber-100 text-amber-700" };
      return { text: `Next: ${label}`, cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200" };
    } catch {
      return { text: "Scheduled", cls: "bg-amber-100 text-amber-700" };
    }
  }
  return { text: "Not scheduled", cls: "bg-muted text-muted-foreground" };
}

export default function NavbarTruckSearch({ compact = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Debounced fetch on input change
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/trucks/search", { params: { q: term, limit: 10 } });
        setResults(Array.isArray(data) ? data : []);
        setHighlight(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const choose = useCallback(
    (r) => {
      if (!r) return;
      // Fire-and-forget click telemetry so admin can see what
      // customers are actually picking. Never block navigation on it.
      try {
        api.post("/trucks/search/click", { q: q.trim(), truck_id: r.truck_id }).catch(() => {});
      } catch { /* no-op */ }
      setOpen(false);
      setQ("");
      setResults([]);
      // Expansion-engine rows always navigate to their precomputed URL.
      if (r.target_url && (r.source === "preloaded" || r.source === "city_teaser")) {
        navigate(r.target_url);
        return;
      }
      if (r.status === "live" || r.status === "scheduled") {
        navigate(`/map?truck=${encodeURIComponent(r.truck_id)}`);
      } else if (r.truck_slug) {
        navigate(`/t/${encodeURIComponent(r.truck_slug)}`);
      } else {
        navigate(`/map?truck=${encodeURIComponent(r.truck_id)}`);
      }
    },
    [navigate, q],
  );

  const onKey = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && q.trim().length >= 2 && results.length === 0 && !loading) {
        // Nothing matched — bounce them to the directory page with the query
        navigate(`/profiles?q=${encodeURIComponent(q.trim())}`);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className={compact ? "relative w-full max-w-xs" : "relative w-full max-w-md"}
      data-testid="navbar-truck-search"
    >
      <div className="relative">
        <MagnifyingGlass
          weight="bold"
          className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Find your favorite truck"
          aria-label="Find your favorite truck"
          className="w-full h-9 pl-8 sm:pl-9 pr-7 sm:pr-9 rounded-full border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition"
          data-testid="navbar-truck-search-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setResults([]); inputRef.current?.focus(); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
            aria-label="Clear search"
            data-testid="navbar-truck-search-clear"
          >
            <X weight="bold" className="h-3 w-3" />
          </button>
        )}
      </div>

      {open && (q.trim().length >= 2) && (
        <div
          // On mobile Chrome/iOS the parent flex container is squeezed
          // by the nav buttons, so `left-0 right-0` collapsed the
          // dropdown to a few pixels wide and made results invisible
          // (real prod bug reported Jul 2026 on iPhone Chrome).
          // Force a minimum width and let the dropdown extend rightward
          // past the input if needed; cap at viewport width so it never
          // overflows off-screen.
          className="absolute left-0 mt-1.5 z-50 min-w-[280px] w-[min(calc(100vw-1rem),24rem)] max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-lg"
          data-testid="navbar-truck-search-dropdown"
        >
          {loading && results.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
              <CircleNotch className="h-4 w-4 animate-spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-5 text-center text-xs text-muted-foreground">
              No trucks match &quot;{q.trim()}&quot;.{" "}
              <button
                type="button"
                onClick={() => navigate(`/profiles?q=${encodeURIComponent(q.trim())}`)}
                className="text-primary font-semibold underline underline-offset-2"
              >
                Browse directory
              </button>
            </div>
          ) : (
            <ul className="py-1.5" role="listbox">
              {results.map((r, i) => {
                const badge = statusLabel(r);
                const logo = fileUrl(r.logo_pin_path || r.logo_path);
                const active = i === highlight;
                const isExpansion = r.source === "preloaded" || r.source === "city_teaser";
                return (
                  <li
                    key={r.truck_id}
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => choose(r)}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${active ? "bg-muted" : ""}`}
                    data-testid={`navbar-truck-search-result-${i}`}
                    data-source={r.source || "live"}
                  >
                    <div className={`flex-none h-9 w-9 rounded-full overflow-hidden grid place-items-center border ${
                      r.source === "city_teaser"
                        ? "bg-primary/10 border-primary/20"
                        : r.source === "preloaded"
                        ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800"
                        : "bg-muted border-border"
                    }`}>
                      {logo ? (
                        <img src={logo} alt="" className="h-full w-full object-cover" />
                      ) : r.source === "city_teaser" ? (
                        <Buildings weight="duotone" className="h-4 w-4 text-primary" />
                      ) : (
                        <MapPin weight="duotone" className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1.5">
                        <span className={`font-semibold text-sm truncate ${isExpansion ? "text-foreground" : ""}`}>
                          {r.truck_name}
                        </span>
                        {r.is_pro && (
                          <span className="text-[9px] font-bold tracking-wider uppercase text-primary border border-primary/30 rounded px-1">
                            Pro
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.source === "city_teaser"
                          ? "Open the city teaser page →"
                          : r.source === "preloaded"
                          ? `${(r.cuisine_tags || []).slice(0, 2).join(" · ") || "Profile"} · not yet claimed`
                          : (r.cuisine_tags || []).slice(0, 3).join(" · ") || "—"}
                      </div>
                    </div>
                    <span className={`flex-none text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>
                      {badge.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
